import { type ComponentType, type ReactNode } from "react";
import { type LucideProps } from "lucide-react";
import { cn } from "@/lib/cn";

export interface EmptyStateProps {
  /** Lucide icon component (rendered ~28px in a tinted circle). */
  icon?: ComponentType<LucideProps>;
  title: ReactNode;
  description?: ReactNode;
  /** Primary call-to-action (usually a <Button>). */
  action?: ReactNode;
  /** Optional secondary action, rendered next to the primary. */
  secondaryAction?: ReactNode;
  className?: string;
}

/**
 * EmptyState — contextual zero-data block (DESIGN-SYSTEM §5, §6.6).
 * One clear CTA. Vertical padding follows the §2.1 empty-state step.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-12 text-center",
        className,
      )}
    >
      {Icon && (
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-elevated/60 text-fg-muted">
          <Icon size={28} strokeWidth={1.75} aria-hidden="true" />
        </span>
      )}
      <div className="space-y-1">
        <p className="text-base font-semibold text-fg-primary">{title}</p>
        {description && (
          <p className="mx-auto max-w-sm text-sm text-fg-muted">{description}</p>
        )}
      </div>
      {(action || secondaryAction) && (
        <div className="mt-1 flex items-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
EmptyState.displayName = "EmptyState";
